import React, { useState, useEffect, useRef } from "react";
import "../App.css";
import "./Chat.css";
import ReactMarkdown from "react-markdown";
import { v4 as uuidv4 } from "uuid";
import { jsPDF } from "jspdf";
import api from "../services/api";
import remarkGfm from "remark-gfm";
import { useOutletContext, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TokenProgressCircle from "../components/TokenProgressCircle";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { Link } from "react-router-dom";

type ErrorKind = "network" | "server" | "auth" | "subscription" | "stream" | "generic";

type Message = {
  text: string;
  sender: "user" | "bot" | "system";
  status?: "final" | "streaming" | "error";
  errorKind?: ErrorKind;
};

type Session = {
  session_key: string;
  created_at?: string;
  title?: string;
  messages: Message[];
};

type OutletCtx = {
  sidebarOpen: boolean;
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleLogout: () => void;
  user: { first_name: string; last_name: string } | null;
};

function Chat() {
  // From Layout
  const [refreshTokenProgress, setRefreshTokenProgress] = useState(0);
  const { sidebarOpen, setSidebarOpen, handleLogout, user } = useOutletContext<OutletCtx>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionFromUrl = searchParams.get('session');

  // Check if user's package allows chat access and is not expired
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/user/profile/me');
        const profile = res.data;
        if (profile?.package?.has_chat === false) {
          navigate(`/${i18n.language}`, { replace: true });
        } else if (profile?.is_expired === true) {
          navigate(`/${i18n.language}/dashboard/subscription`, { replace: true });
        }
      } catch (err) {
        console.error('Failed to check chat access', err);
      }
    })();
  }, [navigate, i18n.language]);

  const [sessions, setSessions] = useState<Session[]>([]);
  const [activeSessionKey, setActiveSessionKey] = useState<string>("");
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(false);

  // Prompts
  const [prompts, setPrompts] = useState<{ id: string; name: string; nameAr: string }[]>([]);
  // mapping session_key => selectedPromptId
  const [selectedPromptMap, setSelectedPromptMap] = useState<Record<string, string>>({});
  // mapping session_key => locked (true after first question)
  const [promptLockedMap, setPromptLockedMap] = useState<Record<string, boolean>>({});

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<{ [key: number]: boolean }>({});
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const bottomRef = useRef<HTMLDivElement>(null);

  // ----------------- RTL detection helper -----------------
  // Returns true if the text contains a significant amount of RTL characters.
  const isRTL = (text: string) => {
    if (!text) return false;
    // Unicode ranges covering Hebrew, Arabic, Syriac, Thaana, etc.
    const rtlRegex = /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/g;
    const matches = text.match(rtlRegex) || [];
    const plain = text.replace(/\s/g, "");
    const total = plain.length || 1; // avoid division by zero
    return matches.length / total > 0.25; // threshold: 25% of chars RTL => treat as RTL
  };

  // --------------- Load sessions ---------------
  const fetchSessions = async (pageNum: number) => {
    try {
      setLoadingSessions(true);
      const res = await api.get<Session[]>("/sessions", {
        params: { page: pageNum, limit: 5 }
      });
      const data = res.data;

      if (!Array.isArray(data)) {
        console.error("Unexpected API response format:", data);
        setSessions([]);
        return;
      }

      if (data.length < 5) {
        setHasMore(false);
      }

      // Ensure messages array exists
      const safeData = data.map(s => ({ ...s, messages: s.messages || [] }));

      setSessions(prev => {
        // If page 1, replace. Else append.
        // Filter out duplicates just in case
        const newSessions = pageNum === 1 ? safeData : [...prev, ...safeData];
        // Remove duplicates by session_key
        const unique = Array.from(new Map(newSessions.map(s => [s.session_key, s])).values());
        return unique;
      });

      if (pageNum === 1 && safeData.length > 0) {
        // If session param in URL, use it; otherwise use first session
        if (sessionFromUrl && safeData.some(s => s.session_key === sessionFromUrl)) {
          setActiveSessionKey(sessionFromUrl);
        } else if (sessionFromUrl) {
          // Session from URL not in list, still set it (will load messages)
          setActiveSessionKey(sessionFromUrl);
        } else {
          setActiveSessionKey(safeData[0].session_key);
        }
      } else if (pageNum === 1 && safeData.length === 0) {
        // No sessions at all, create new
        const newKey = uuidv4();
        const newSession: Session = { session_key: newKey, messages: [] };
        setSessions([newSession]);
        setActiveSessionKey(newKey);
      }

      // Initialize promptLockedMap
      const newLocked: Record<string, boolean> = {};
      safeData.forEach((s) => {
        newLocked[s.session_key] = (s.messages?.length || 0) > 0;
      });
      setPromptLockedMap((prev) => ({ ...newLocked, ...prev }));

    } catch (err) {
      console.error(t("chat.errorLoadingSessions"), err);
      if (pageNum === 1) {
        // fallback
        const newKey = uuidv4();
        const newSession: Session = { session_key: newKey, messages: [] };
        setSessions([newSession]);
        setActiveSessionKey(newKey);
      }
    } finally {
      setLoadingSessions(false);
    }
  };

  useEffect(() => {
    fetchSessions(1);
  }, []);

  const loadMoreSessions = () => {
    if (!hasMore || loadingSessions) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchSessions(nextPage);
  };

  // --------------- Load prompts ---------------
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ id: string; name: string, nameAr: string }[]>("/prompts");
        setPrompts(res.data || []);
      } catch (err) {
        console.error(t("chat.errorLoadingSessions"), err);
      }
    })();
  }, [t]);

  // --------------- Load messages of active session ---------------
  useEffect(() => {
    if (!activeSessionKey) return;
    (async () => {
      try {
        const res = await api.get<Message[]>(`/sessions/${activeSessionKey}/messages`, {
          params: { session_key: activeSessionKey },
        });
        const msgs = res.data || [];
        setSessions((prev) =>
          prev.map((s) => (s.session_key === activeSessionKey ? { ...s, messages: msgs } : s))
        );

        // Si la session a déjà des messages, verrouiller la sélection
        setPromptLockedMap((prev) => ({
          ...prev,
          [activeSessionKey]: (msgs.length || 0) > 0 ? true : prev[activeSessionKey] ?? false,
        }));
      } catch (err) {
        console.error(t("chat.errorLoadingSessions"), err);
      }
    })();
  }, [activeSessionKey, t]);

  const activeSession = sessions.find((s) => s.session_key === activeSessionKey);

  // --------------- Auto-scroll ---------------
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [activeSession?.messages, isTyping]);

  // --------------- Sidebar actions ---------------
  const createNewSession = () => {
    // Only block if there is a LOCAL empty session (no created_at and no messages)
    const hasEmpty = sessions.some((s) => !s.created_at && s.messages.length === 0);
    if (hasEmpty) {
      return;
    }

    const newKey = uuidv4();
    const newSession: Session = { session_key: newKey, messages: [] };
    setSessions((prev) => [newSession, ...prev]);

    // ensure prompt state cleared for this new session
    setSelectedPromptMap((prev) => ({ ...prev, [newKey]: "" }));
    setPromptLockedMap((prev) => ({ ...prev, [newKey]: false }));

    setActiveSessionKey(newKey);
    if (window.innerWidth < 768) setSidebarOpen(false);
  };

  const handleSessionClick = (key: string) => {
    setActiveSessionKey(key);
    if (window.innerWidth < 768) setSidebarOpen(false); // auto-close on mobile
  };

  const handleDeleteSession = async (session_key: string) => {
    const confirmDelete = window.confirm(t("chat.confirmDeleteSession"));
    if (!confirmDelete) return;

    const session = sessions.find((s) => s.session_key === session_key);
    // If it has created_at, it exists on server. If it has messages, it exists (or will/should).
    // "isEmpty" false implies "Exists on Backend"
    const existsOnBackend = !!(session?.created_at || (session?.messages && session.messages.length > 0));

    try {
      if (existsOnBackend) {
        // Session existe côté backend
        await api.delete(`/sessions/${session_key}`);
      }

      // Supprimer localement
      setSessions((prev) => {
        const filtered = prev.filter((s) => s.session_key !== session_key);

        // cleanup prompt maps
        setSelectedPromptMap((prevMap) => {
          const copy = { ...prevMap };
          delete copy[session_key];
          return copy;
        });
        setPromptLockedMap((prevMap) => {
          const copy = { ...prevMap };
          delete copy[session_key];
          return copy;
        });

        // Si on supprime la session active
        if (session_key === activeSessionKey) {
          if (filtered.length > 0) {
            setActiveSessionKey(filtered[0].session_key);
          } else {
            const newKey = uuidv4();
            const newSession: Session = { session_key: newKey, messages: [] };
            setActiveSessionKey(newKey);
            // initialize maps for new session
            setSelectedPromptMap({ [newKey]: "" });
            setPromptLockedMap({ [newKey]: false });
            return [newSession];
          }
        }
        return filtered;
      });

      showToast(t("chat.sessionDeleted"));
    } catch (err) {
      console.error(err);
      showToast(t("chat.errorDeletingSession"));
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile || !activeSessionKey) return;

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("session_key", activeSessionKey);

    const token = localStorage.getItem("token");

    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/chat/upload`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    const data = await res.json();
    if (data.success) {
      showToast(t("chat.fileUploadedSuccess"));
    } else {
      showToast(t("chat.errorUploadingFile"));
    }
  };

  // --------------- Chat send / stream ---------------
  const appendErrorMessage = (sessionKey: string, errorKind: ErrorKind) => {
    setSessions((prev) =>
      prev.map((s) => {
        if (s.session_key !== sessionKey) return s;
        const finalizedMsgs = s.messages.map((m) =>
          m.status === "streaming" ? { ...m, status: "final" as const } : m
        );
        return {
          ...s,
          messages: [
            ...finalizedMsgs,
            { text: "", sender: "system", status: "error", errorKind },
          ],
        };
      })
    );
  };

  const sendQuestion = async (question: string) => {
    if (!question.trim() || !activeSessionKey) return;

    // Si première question de la session, forcer sélection d'un prompt
    const currentSession = sessions.find((s) => s.session_key === activeSessionKey);
    const isFirstQuestion = currentSession ? (currentSession.messages?.length || 0) === 0 : true;
    const selectedPromptForThisSession = selectedPromptMap[activeSessionKey] || "";

    if (isFirstQuestion && !selectedPromptForThisSession) {
      showToast(t("chat.selectAnalysisType"));
      return;
    }

    const userMessage: Message = {
      text: question,
      sender: "user",
      status: "final",
    };

    // Ajouter le message de l'utilisateur localement
    setSessions((prev) =>
      prev.map((s) =>
        s.session_key === activeSessionKey
          ? { ...s, messages: [...s.messages, userMessage] }
          : s
      )
    );

    const controller = new AbortController();
    setAbortController(controller);
    setIsTyping(true);

    // Buffer pour le streaming
    let streamed = "";
    let receivedAnyChunk = false;

    try {
      const token = localStorage.getItem("token");

      // ✅ Construction du FormData
      const formData = new FormData();
      formData.append("question", question);
      formData.append("session_key", activeSessionKey);
      if (selectedFile) {
        formData.append("file", selectedFile);
      }

      // ✅ Envoyer le prompt choisi uniquement à la première question
      if (isFirstQuestion && selectedPromptForThisSession) {
        formData.append("promptId", selectedPromptForThisSession);
        // locker ce select pour cette session
        setPromptLockedMap((prev) => ({ ...prev, [activeSessionKey]: true }));
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/chat/ask`, {
        method: "POST",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          // ⚠️ NE PAS mettre Content-Type → FormData le gère automatiquement
        },
        body: formData,
        signal: controller.signal,
      });

      if (!response.ok) {
        let errorKind: ErrorKind = "generic";
        if (response.status === 401) errorKind = "auth";
        else if (response.status === 402) errorKind = "subscription";
        else if (response.status >= 500) errorKind = "server";
        appendErrorMessage(activeSessionKey, errorKind);
        return;
      }
      if (!response.body) {
        appendErrorMessage(activeSessionKey, "generic");
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      // Ajouter un message bot "streaming"
      setSessions((prev) =>
        prev.map((s) =>
          s.session_key === activeSessionKey
            ? {
              ...s,
              messages: [...s.messages, { text: "", sender: "bot", status: "streaming" }],
            }
            : s
        )
      );

      let done = false;
      while (!done) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;
        if (!value) continue;
        receivedAnyChunk = true;

        const chunk = decoder.decode(value);
        streamed += chunk;

        // Mettre à jour le dernier message bot en cours de streaming
        setSessions((prev) =>
          prev.map((s) => {
            if (s.session_key !== activeSessionKey) return s;
            const msgs = [...s.messages];
            const last = msgs[msgs.length - 1];
            if (last?.status === "streaming") {
              last.text = streamed;
            }
            return { ...s, messages: msgs };
          })
        );
      }

      // Transformer le dernier message "streaming" en "final"
      setSessions((prev) =>
        prev.map((s) => {
          if (s.session_key !== activeSessionKey) return s;
          const msgs = [...s.messages];
          const last = msgs[msgs.length - 1];
          if (last?.status === "streaming") last.status = "final";
          return { ...s, messages: msgs };
        })
      );
    } catch (e) {
      if ((e as any).name === "AbortError") {
        // Arrêt volontaire par l'utilisateur — finaliser le partiel et afficher un toast
        setSessions((prev) =>
          prev.map((s) => {
            if (s.session_key !== activeSessionKey) return s;
            const msgs = [...s.messages];
            const last = msgs[msgs.length - 1];
            if (last?.status === "streaming") last.status = "final";
            return { ...s, messages: msgs };
          })
        );
        showToast(t("chat.responseInterrupted"));
      } else {
        console.error(e);
        // TypeError = fetch a échoué (offline, DNS, CORS) → réseau
        // Sinon, si on avait commencé à recevoir des chunks → stream coupé
        const isNetworkErr = e instanceof TypeError;
        const errorKind: ErrorKind = isNetworkErr
          ? "network"
          : receivedAnyChunk
            ? "stream"
            : "network";
        appendErrorMessage(activeSessionKey, errorKind);
      }
    } finally {
      setIsTyping(false);
      setAbortController(null);
      setSelectedFile(null);
      setRefreshTokenProgress(v => v + 1);
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;
    const question = input;
    setInput("");
    await sendQuestion(question);
  };

  const retryLastMessage = () => {
    if (isTyping) return;
    const session = sessions.find((s) => s.session_key === activeSessionKey);
    if (!session) return;

    let lastUserIdx = -1;
    for (let i = session.messages.length - 1; i >= 0; i--) {
      if (session.messages[i].sender === "user") {
        lastUserIdx = i;
        break;
      }
    }
    if (lastUserIdx === -1) return;

    const lastUserText = session.messages[lastUserIdx].text;

    // Retirer l'ancien message utilisateur + tout ce qui suit (partiel bot, erreur)
    setSessions((prev) =>
      prev.map((s) =>
        s.session_key === activeSessionKey
          ? { ...s, messages: s.messages.slice(0, lastUserIdx) }
          : s
      )
    );

    // Renvoyer après que le state soit appliqué
    setTimeout(() => sendQuestion(lastUserText), 0);
  };

  // --------------- UI helpers ---------------
  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), 2000);
  };

  const fallbackCopy = (text: string) => {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand("copy");
      showToast(t("chat.responseCopied"));
    } catch {
      showToast(t("chat.cannotCopy"));
    }
    document.body.removeChild(textarea);
  };

  const handleCopy = (text: string) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(
        () => showToast(t("chat.responseCopied")),
        () => fallbackCopy(text)
      );
    } else {
      fallbackCopy(text);
    }
  };

  const handlePdf = (text: string) => {
    const doc = new jsPDF();
    const lines = doc.splitTextToSize(text, 180);
    doc.text(lines, 10, 10);
    doc.save("response.pdf");
  };

  const toggleExpand = (i: number) => {
    setExpanded((prev) => {
      const current = prev[i] ?? true; // default expanded
      return { ...prev, [i]: !current };
    });
  };

  // handle select change for current session
  const handlePromptChange = (session_key: string, promptId: string) => {
    setSelectedPromptMap((prev) => ({ ...prev, [session_key]: promptId }));
  };

  // --------------- Render ---------------
  return (
    <div className="main-container">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay d-md-none"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`chat-sidebar ${sidebarOpen ? "open" : "d-none"} d-md-flex flex-column`}
      >
        {/* Mobile Menu Items */}
        <div className="d-md-none mb-3 border-bottom pb-3 p-3">
          {user && (
            <div className="d-flex align-items-center mb-3">
              <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-2 text-white" style={{ width: 32, height: 32 }}>
                {user.first_name?.charAt(0).toUpperCase()}
              </div>
              <Link to={`/${i18n.language}/profile`} className="text-dark text-decoration-none fw-bold">
                {user.first_name} {user.last_name}
              </Link>
            </div>
          )}

          <Link to={`/${i18n.language}`} className="btn btn-outline-secondary w-100 mb-2 text-start">
            <i className="bi bi-house-door me-2"></i> {t("nav.home", "Accueil")}
          </Link>

          <div className="d-flex justify-content-center">
            <LanguageSwitcher variant="white-bg" />
          </div>
        </div>

        {/* New Session Button */}
        <div className="sidebar-header">
          <button className="new-chat-btn" onClick={createNewSession}>
            <i className="bi bi-plus-lg"></i> {t("chat.newSession")}
          </button>
        </div>

        {/* Sessions list */}
        <div className="sessions-container">
          <div className="sessions-title">{t("chat.recentSessions", "Conversations récentes")}</div>
          {sessions.map((s) => (
            <div
              key={s.session_key}
              className={`session-item ${s.session_key === activeSessionKey ? "active" : ""}`}
              onClick={() => handleSessionClick(s.session_key)}
            >
              <div className="session-icon">
                <i className="bi bi-chat-text"></i>
              </div>
              <div className="session-content">
                <div className="session-title">{s.title?.slice(0, 25) || t("chat.newSession")}</div>
                <div className="session-date">
                  {s.created_at ? new Date(s.created_at).toLocaleDateString(i18n.language === 'ar' ? 'ar-DZ' : 'fr-FR') : ''}
                </div>
              </div>
              <button
                className="session-delete"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteSession(s.session_key);
                }}
                title={t("chat.deleteSessionTitle")}
              >
                <i className="bi bi-trash"></i>
              </button>
            </div>
          ))}

          {hasMore && (
            <button
              className="btn btn-sm btn-outline-secondary w-100 mt-2"
              onClick={loadMoreSessions}
              disabled={loadingSessions}
            >
              {loadingSessions ? "..." : t("chat.loadMore")}
            </button>
          )}
        </div>

        {/* Footer with token progress */}
        <div className="sidebar-footer">
          <div className="token-box">
            <TokenProgressCircle refreshTrigger={refreshTokenProgress} />
          </div>
        </div>
      </div>

      {/* Chat area */}
      <div className="chat-main">
        {/* Messages */}
        <div className="chat-messages-container">
          {(activeSession?.messages?.length || 0) === 0 ? (
            <div className="chat-welcome">
              <div className="welcome-icon">
                <i className="bi bi-robot"></i>
              </div>
              <h2 className="welcome-title">{t("chat.welcomeTitle", "Bienvenue sur Berti Law")}</h2>
              <p className="welcome-subtitle">{t("chat.firstQuestionPrompt")}</p>
            </div>
          ) : (
            activeSession?.messages?.map((m, i) => {
              // Rendu spécial pour les messages d'erreur système
              if (m.sender === "system" && m.status === "error") {
                const kind: ErrorKind = m.errorKind || "generic";
                const messageKey =
                  kind === "network" ? "chat.errorNetwork" :
                  kind === "server" ? "chat.errorServer" :
                  kind === "auth" ? "chat.errorAuth" :
                  kind === "subscription" ? "chat.errorSubscription" :
                  kind === "stream" ? "chat.errorStream" :
                  "chat.errorUnknown";

                const isAuth = kind === "auth";
                const isSubscription = kind === "subscription";
                const actionLabel = isAuth
                  ? t("chat.reconnect")
                  : isSubscription
                    ? t("chat.renewSubscription")
                    : t("chat.retryLastMessage");
                const actionIcon = isAuth
                  ? "bi-box-arrow-in-right"
                  : isSubscription
                    ? "bi-credit-card"
                    : "bi-arrow-clockwise";
                const onAction = () => {
                  if (isAuth) {
                    localStorage.removeItem("token");
                    navigate(`/${i18n.language}/login`);
                  } else if (isSubscription) {
                    navigate(`/${i18n.language}/dashboard/subscription`);
                  } else {
                    retryLastMessage();
                  }
                };

                return (
                  <div
                    key={i}
                    className="d-flex justify-content-center my-3"
                    role="alert"
                  >
                    <div
                      className="alert alert-warning d-flex flex-column align-items-start mb-0"
                      style={{ maxWidth: "85%", borderRadius: 12 }}
                    >
                      <div className="d-flex align-items-center mb-2">
                        <i className="bi bi-exclamation-triangle-fill me-2" style={{ fontSize: "1.2rem" }}></i>
                        <strong>{t("chat.errorTitle")}</strong>
                      </div>
                      <div className="mb-2" style={{ fontSize: "0.95rem" }}>
                        {t(messageKey)}
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-0"
                        onClick={onAction}
                        disabled={!isAuth && !isSubscription && isTyping}
                        style={{ textDecoration: "underline" }}
                      >
                        <i className={`bi ${actionIcon} me-1`}></i>
                        {actionLabel}
                      </button>
                    </div>
                  </div>
                );
              }

              const isBot = m.sender === "bot";
              const isExpanded = expanded[i] ?? true;
              const msgIsRTL = isRTL(m.text);

              return (
                <div
                  key={i}
                  className={`mb-3 d-flex ${m.sender === "user" ? "justify-content-start" : "justify-content-end"}`}
                  onMouseEnter={() => isBot && setHoveredIndex(i)}
                  onMouseLeave={() => isBot && setHoveredIndex(null)}
                  style={{ position: "relative" }}
                >
                  <div
                    // set direction and alignment dynamically based on message text direction
                    dir={msgIsRTL ? "rtl" : "ltr"}
                    className={`p-2 rounded message-bubble ${isBot ? "bg-light border" : "p-2 rounded-5 px-3 bg-primary text-white"}`}
                    style={{
                      maxHeight: isBot && !isExpanded ? 200 : "none",
                      overflow: isBot && !isExpanded ? "hidden" : "visible",
                      textAlign: msgIsRTL ? "right" : "left",
                      direction: msgIsRTL ? "rtl" : "ltr",
                    }}
                  >
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      className="markdown-body"
                      components={{
                        a: ({ node, ...props }) => (
                          <a
                            {...props}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "blue", textDecoration: "underline" }}
                          >
                            {props.children}
                          </a>
                        ),
                      }}
                    >
                      {m.text}
                    </ReactMarkdown>

                    {isBot && hoveredIndex === i && (
                      <div
                        style={{
                          position: "absolute",
                          bottom: 4,
                          ...(i18n.language === 'ar' ? { left: 4 } : { right: 4 }),
                          display: "flex",
                          gap: "6px",
                        }}
                      >
                        <button
                          className="btn btn-sm btn-light border"
                          onClick={() => handleCopy(m.text)}
                          title={t("chat.copy")}
                        >
                          <i className="bi bi-clipboard"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-light border"
                          onClick={() => handlePdf(m.text)}
                          title={t("chat.exportPDFTitle")}
                        >
                          <i className="bi bi-file-earmark-text"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-light border"
                          onClick={() => toggleExpand(i)}
                          title={isExpanded ? t("chat.reduce") : t("chat.expand")}
                        >
                          {isExpanded ? (
                            <i className="bi bi-arrows-angle-contract"></i>
                          ) : (
                            <i className="bi bi-arrows-fullscreen"></i>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}

          {isTyping && (
            <div className="typing-indicator">
              <div className="typing-dots">
                <span></span><span></span><span></span>
              </div>
              <span>{t("chat.analyzing")}</span>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="chat-input-container">
          {activeSession && (
            <div className="prompt-selector">
              <select
                id="promptSelect"
                className="prompt-dropdown"
                value={selectedPromptMap[activeSessionKey] || ""}
                onChange={(e) => handlePromptChange(activeSessionKey, e.target.value)}
                disabled={promptLockedMap[activeSessionKey] === true || isTyping}
              >
                <option value="">{t("chat.selectAnalysis")}</option>
                {prompts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {i18n.language === 'ar' ? p.nameAr : p.name}
                  </option>
                ))}
              </select>

              {promptLockedMap[activeSessionKey] && (
                <div className="text-muted small mt-1">
                  <i className="bi bi-lock-fill me-1"></i> {t("chat.analysisTypeLocked")}
                </div>
              )}
            </div>
          )}

          {selectedFile && (
            <div className="text-muted small mb-2">
              <i className="bi bi-paperclip me-2"></i> {selectedFile.name}
            </div>
          )}

          <div className="input-wrapper">
            <textarea
              className="chat-textarea-new"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={t("chat.writeYourQuestion")}
              rows={5}
              disabled={isTyping}
              dir={i18n.language === 'ar' ? "rtl" : "ltr"}
              style={{ textAlign: i18n.language === 'ar' ? "right" : "left" }}
            />

            <div className="input-actions">
              <input
                type="file"
                id="fileUpload"
                className="d-none"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              />
              <label htmlFor="fileUpload" className="send-btn" style={{ background: '#e9ecef', color: '#6c757d' }}>
                <i className="bi bi-paperclip"></i>
              </label>

              {!isTyping ? (
                <button className="send-btn" onClick={handleSend}>
                  <i className="bi bi-send-fill"></i>
                </button>
              ) : (
                <button className="send-btn stop-btn" onClick={() => abortController?.abort()}>
                  <i className="bi bi-stop-fill"></i>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Toast */}
        {toastMessage && (
          <div
            style={{
              position: "fixed",
              bottom: 20,
              left: i18n.language === 'ar' ? 20 : 'auto',
              right: i18n.language === 'ar' ? 'auto' : 20,
              background: "rgba(0,0,0,0.8)",
              color: "white",
              padding: "10px 16px",
              borderRadius: 6,
              zIndex: 9999,
              fontSize: "0.9rem",
            }}
          >
            {toastMessage}
          </div>
        )}
      </div>
    </div>
  );
}

export default Chat;
